import * as React from "react";

import { Box, makeStyles, Theme, Typography } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { AllMerchantFields, Field, ValueOf } from "client/lib/apt-merchant";

import { ThemedRadio } from "./ThemedRadio";

const useStyles = makeStyles((theme: Theme) => {
  const {
    gray40,
    radioLabelWidth,
    radioWrapperDisplay,
    radioHeaderColor,
    radiosWrapperMarginLeft,
  } = useNormalizedBrandPaletteValues(theme);

  return {
    root: {
      display: "flex",
      flexDirection: "column",
    },
    inputLabel: {
      color: radioHeaderColor,
      maxWidth: radioLabelWidth,
      overflow: "auto",
      fontSize: `${14 / 16}rem`,
      lineHeight: "22px",
    },
    inputWrapper: {
      display: radioWrapperDisplay,
      alignItems: "center",
      width: "100%",
      justifyContent: "space-between",
    },
    radiosWrapper: {
      display: radioWrapperDisplay,
      marginLeft: radiosWrapperMarginLeft,
      gap: "8px",
    },
    icon: {
      width: 26,
      height: 26,
      border: `1px solid ${gray40}`,
      borderRadius: "100px",
    },
    error: {
      fontSize: `${14 / 16}rem`,
    },
  };
});

interface RadioProps {
  errorMessage?: string;
  field: Field<ValueOf<AllMerchantFields>>;
  handleChange: (value: string) => void;
}

export const RadioInputField: React.FC<RadioProps> = ({
  errorMessage,
  field,
  handleChange,
}) => {
  const classes = useStyles();
  const [selectedValue, setSelectedValue] = React.useState(field?.value ?? "");

  React.useEffect(() => {
    if (typeof field.value !== "undefined") {
      setSelectedValue(field.value);
    }
  }, [field.value]);

  const updateValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
    handleChange(event.target.value);
  };

  return (
    <Box data-testid={field.propertyName} className={classes.root}>
      <Box className={classes.inputWrapper}>
        <Typography id={field.propertyName} className={classes.inputLabel}>
          {field.i18nValues.label}
        </Typography>
        <Box className={classes.radiosWrapper}>
          <ThemedRadio
            checked={selectedValue === "Yes"}
            checkedIcon={<CheckCircleIcon fontSize="medium" />}
            inputProps={{
              "aria-label": "yes",
              id: `${field.propertyName}-yes`,
            }}
            icon={<span className={classes.icon} />}
            name="radio-button-yes"
            onChange={updateValue}
            value="Yes"
          />
          <ThemedRadio
            checked={selectedValue === "No"}
            checkedIcon={<CheckCircleIcon fontSize="inherit" />}
            inputProps={{
              "aria-label": "no",
              id: `${field.propertyName}-no`,
            }}
            icon={<span className={classes.icon} />}
            name="radio-button-no"
            onChange={updateValue}
            value="No"
          />
        </Box>
      </Box>
      {Boolean(errorMessage) && (
        <Typography className={classes.error} color="error">
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};
